/**
 * BeamDesignerWorkspace - shared beam analysis workspace.
 *
 * Orchestrates all state and handlers, passing them as props to the tab components.
 * The material is fixed by the hosting page (concrete / wood / steel); this component
 * is composed by the per-material page shells in src/pages/{Concrete,Wood,Steel}Beam.
 */

import React, { useState, useCallback, useEffect } from 'react'
import { SMBeamModel, SMBeamNode, UiNotice, LimitStateSelection, TabType, CrossSection, CrossSectionTemplateResponse, BeamMaterial } from './types'
import { getFilteredSectionPresets as filterSectionPresetsByMaterial, createHandlers, createLoadHandlers, defaultSectionCatalog } from './handlers'
import useMaterialState from './hooks/useMaterialState'
import useResultsViewState from './hooks/useResultsViewState'
import useResponseViewState from './hooks/useResponseViewState'
import useLoadState from './hooks/useLoadState'
import useAnalysisActions from './hooks/useAnalysisActions'
import { createInitialModel } from './initialState'
import { loadPersistedModel, persistModel, BEAM_TEMPLATE_STORAGE_KEY } from './modelPersistence'
import { calculateSectionProperties } from './utils/sectionProperties'
import { BeamTemplateData, createModelFromTemplate } from './templateModel'
import { getSessionHeaders as sharedGetSessionHeaders } from '@structura/services'

// Import tab components
import SMBeamDesignerTabContent from './TabContent'
import DesignManagerBar from './components/DesignManagerBar'

// Import Error Boundary
import { ErrorBoundary } from '@structura/ui'
import { useTranslation, TranslationKey } from '@structura/i18n'

// Import styles
import './styles.css'

interface BeamDesignerWorkspaceProps {
  material: BeamMaterial
  // Optional: forwarded to DesignManagerBar so the host app can share a saved design.
  onShareDesign?: (design: { id: string; name: string }) => void
}

const BeamDesignerWorkspace: React.FC<BeamDesignerWorkspaceProps> = ({ material, onShareDesign }) => {
  const { t } = useTranslation()

  const [initialTemplateData] = useState<BeamTemplateData | null>(() => {
    const templateJson = sessionStorage.getItem(BEAM_TEMPLATE_STORAGE_KEY)
    if (templateJson) {
      try {
        return JSON.parse(templateJson) as BeamTemplateData
      } catch {
        return null
      }
    }
    return null
  })
  // A template only belongs to the page whose material matches it. A mismatch
  // can only occur if stale sessionStorage from a different material leaked in
  // (the three apps are deployed on separate subdomains, so this is rare in
  // practice). Cross-app navigation isn't possible from the SPA — apps are
  // independent with their own route tables — so we drop the stale template
  // and continue in the current app with the default model.
  const templateMismatch: boolean = initialTemplateData != null && initialTemplateData.material !== material
  useEffect(() => {
    if (initialTemplateData != null) {
      sessionStorage.removeItem(BEAM_TEMPLATE_STORAGE_KEY)
    }
    if (templateMismatch && initialTemplateData != null) {
      console.warn(`Discarded stale beam template: stored material '${initialTemplateData.material}' does not match current app material '${material}'.`)
    }
  }, [initialTemplateData, templateMismatch, material])

  const [model, setModel] = useState<SMBeamModel>(() => {
    // An explicitly chosen template (from Structura-Bits) always wins — the user
    // just asked to load that example. Otherwise restore the model the user was
    // working on before navigating away, falling back to a fresh default.
    if (initialTemplateData && initialTemplateData.material === material) {
      return createModelFromTemplate(initialTemplateData)
    }
    const persisted: SMBeamModel | null = loadPersistedModel(material)
    if (persisted) {
      return persisted
    }
    return createInitialModel()
  })

  // Persist the working model on every change so it survives in-app navigation.
  useEffect(() => {
    persistModel(material, model)
  }, [material, model])
  // appliedTemplate state removed - was unused (templateId is consumed via initialTemplateData)
  const [sectionCatalog, setSectionCatalog] = useState<Record<string, CrossSection>>(defaultSectionCatalog)
  const [sectionCatalogLoading, setSectionCatalogLoading] = useState<boolean>(false)
  const [sectionCatalogError, setSectionCatalogError] = useState<string | null>(null)

  // ========== TAB AND UI STATE ==========
  
  const [activeStep, setActiveStep] = useState<TabType>('geometry')
  const resultsView = useResultsViewState()

  const responseView = useResponseViewState()

  const convertTemplatesToCatalog = useCallback((templates: CrossSectionTemplateResponse[]): Record<string, CrossSection> => {
    const catalog: Record<string, CrossSection> = {}
    templates.forEach(template => {
      const material = (template.materialType || 'steel').toLowerCase()
      if (material !== 'steel' && material !== 'concrete' && material !== 'timber') {
        return
      }

      const key = template.templateKey && template.templateKey.length > 0 ? template.templateKey : template.id

      const normalizeSectionType = (raw: string | undefined): CrossSection['sectionType'] | undefined => {
        if (!raw) {
          return undefined
        }
        const v = raw.toLowerCase()
        switch (v) {
          case 'i-beam':
            return 'I-beam'
          case 'h-beam':
            return 'H-beam'
          case 'rectangular':
            return 'rectangular'
          case 'circular':
            return 'circular'
          case 't-beam':
            return 'T-beam'
          case 'l-beam':
            return 'L-beam'
          default:
            return undefined
        }
      }

      const normalizeSectionClass = (raw: string | undefined): CrossSection['sectionClass'] | undefined => {
        if (!raw || raw.length === 0) {
          return 'auto'
        }
        if (raw === '1' || raw === '2' || raw === '3' || raw === '4') {
          return raw
        }
        return 'auto'
      }
      const baseSection: CrossSection = {
        name: template.name,
        width: template.widthMillimeters,
        height: template.heightMillimeters,
        area: template.areaSquareMeters,
        inertia: template.inertiaStrongAxisMeters4,
        inertiaZ: template.inertiaWeakAxisMeters4,
        isCustom: false,
        key,
        material,
        // Tretype is authoritative from the backend (CrossSectionProfile.TimberKind); used to filter
        // the section list to the selected timber type. Non-timber templates carry no kind.
        timberKind: template.timberKind,
        sectionType: normalizeSectionType(template.sectionType),
        webThickness: template.webThicknessMillimeters || undefined,
        flangeThickness: template.flangeThicknessMillimeters || undefined,
        sectionClass: normalizeSectionClass(template.sectionClass)
      }

      const derived = calculateSectionProperties(baseSection, material as 'steel' | 'concrete' | 'timber')
      catalog[key] = {
        ...baseSection,
        ...derived
      }
    })

    return catalog
  }, [])

  useEffect(() => {
    setModel(prev => {
      if (!prev.crossSection || prev.crossSection.isCustom || !prev.crossSection.key) {
        return prev
      }

      const canonical = sectionCatalog[prev.crossSection.key]
      if (!canonical) {
        return prev
      }

      const hasDifference = canonical.area !== prev.crossSection.area ||
        canonical.inertia !== prev.crossSection.inertia ||
        canonical.height !== prev.crossSection.height ||
        canonical.width !== prev.crossSection.width

      if (!hasDifference) {
        return prev
      }

      return {
        ...prev,
        crossSection: canonical,
        spans: prev.spans.map(span => ({
          ...span,
          A: canonical.area,
          I: canonical.inertia
        }))
      }
    })
  }, [sectionCatalog, setModel])

  const getFilteredSectionPresets = useCallback((material: string) => {
    return filterSectionPresetsByMaterial(sectionCatalog, material)
  }, [sectionCatalog])

  const loadState = useLoadState(model)
  const {
    selectedSpan,
    setSelectedSpan,
    selectedLoadType,
    loadForm,
    setLoadForm
  } = loadState

  // callbacks defined after handler creation
  
  const [selectedLimitState, setSelectedLimitState] = useState<LimitStateSelection>('ULS')
  
  const [supportNotice, setSupportNotice] = useState<UiNotice | null>(null)

  // Update form when selected span changes
  const materialState = useMaterialState({ model, setModel, getFilteredSectionPresets, lockedMaterial: material })
  const {
    currentMaterial,
    nationalAnnex,
    selectedSteelGrade,
    selectedSteelSubgrade,
    steelThickness,
    selectedConcreteClass,
    selectedReinfClass,
    selectedPrestressClass,
    timberKind,
    selectTimberKind,
    selectedTimberClass,
    timberOptions,
    timberLoadDuration,
    timberServiceClass,
    concreteRelativeHumidity,
    concreteNotionalSize,
    concreteLoadingAge,
    concreteCreepCoefficient,
    concreteEffectiveModulus,
    materialDetails,
    calculateResolvedSelfWeight,
    sectionMaterialStrength,
    codeRequirementsPayload
  } = materialState

  // Session headers -- delegated to shared utility (sessionStorage-based, per-session identity)
  const getSessionHeaders = useCallback(() => sharedGetSessionHeaders(), [])

  useEffect(() => {
    let cancelled = false

    const loadCatalog = async () => {
      setSectionCatalogLoading(true)
      setSectionCatalogError(null)

      try {
        const response = await fetch('/api/CrossSection/templates', {
          headers: getSessionHeaders()
        })

        if (!response.ok) {
          throw new Error(`Failed to load cross sections (HTTP ${response.status})`)
        }

        const data: CrossSectionTemplateResponse[] = await response.json()
        if (cancelled) {
          return
        }

        const catalog = convertTemplatesToCatalog(data)
        if (Object.keys(catalog).length > 0) {
          setSectionCatalog(catalog)
        } else {
          // An empty template table must not silently leave the hardcoded placeholder catalog
          // selectable — its section properties are approximations, not the DB-backed values.
          setSectionCatalog({})
          setSectionCatalogError('Cross-section template catalog is empty. Seed the CrossSection templates in the backend database.')
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Failed to load cross sections'
          // Backend is the authoritative source of section properties: on failure the
          // placeholder presets are withdrawn instead of being analyzed as if DB-backed.
          setSectionCatalog({})
          setSectionCatalogError(message)
        }
      } finally {
        if (!cancelled) {
          setSectionCatalogLoading(false)
        }
      }
    }

    loadCatalog()

    return () => {
      cancelled = true
    }
  }, [convertTemplatesToCatalog, getSessionHeaders])

  const analysisActions = useAnalysisActions({
    model,
    setModel,
    material: {
      currentMaterial,
      nationalAnnex,
      selectedSteelGrade,
      selectedSteelSubgrade,
      steelThickness,
      selectedConcreteClass,
      selectedReinfClass,
      selectedPrestressClass,
      timberKind,
      selectedTimberClass,
      timberOptions,
      timberLoadDuration,
      timberServiceClass,
      concreteRelativeHumidity,
      concreteNotionalSize,
      concreteLoadingAge,
      concreteCreepCoefficient,
      concreteEffectiveModulus,
      materialDetails  // Include auto-fetched material properties
    },
    codeRequirements: codeRequirementsPayload,
    selectedLimitState,
    getSessionHeaders,
    setActiveStep,
    t
  })

  const {
    result,
    setResult,
    invalidateResults,
    loading,
    analyzingAll,
    analyzeAllLimitStates,
    analyzingTimoshenko,
    analyzeTimoshenko,
    analysisTheory,
    lastAnalysisSummary,
    selectedCombinationId,
    setSelectedCombinationId,
    calculatingResponse,
    calculateResponse,
    calculatingSecondOrderEffects,
    calculateSecondOrderEffects,
    secondOrderEffectsResult,
    calculatingDynamics,
    calculateDynamics,
    dynamicsResult
  } = analysisActions

  // ========== SESSION MANAGEMENT ==========
  // Session ID is now created on-demand by services/sessionUtils.ts (sessionStorage-based)

  // ========== CREATE HANDLERS ==========
  
  // Geometry and load edits invalidate the whole analysis group — displayed result, the
  // limit-state group, derived results — and mark in-flight requests stale.
  const clearResults = invalidateResults
  
  const geometryHandlers = createHandlers(model, setModel, currentMaterial, calculateResolvedSelfWeight, setSelectedSpan, setSupportNotice, clearResults)
  const loadHandlers = createLoadHandlers(model, setModel, calculateResolvedSelfWeight, selectedSpan, selectedLoadType, loadForm, setLoadForm, clearResults)

  const geometryCallbacks = {
    addSpan: geometryHandlers.addSpan,
    removeSpan: geometryHandlers.removeSpan,
    updateNodeSupport: geometryHandlers.updateNodeSupport,
    updateSpanLength: geometryHandlers.updateSpanLength
  }

  const loadCallbacks = {
    addLoadToSpan: loadHandlers.addLoadToSpan,
    removeLoad: loadHandlers.removeLoad,
    editLoad: loadHandlers.editLoad
  }

  // ========== ANALYSIS HANDLER ==========

  // Analysis, response, second-order, and dynamics operations are managed by useAnalysisActions.

  // ========== SUPPORT HELPERS ==========
  
  const getSupportIcon = (supportType: SMBeamNode['supportType']) => {
    switch (supportType) {
      case 'Fixed': return '🔒'
      case 'Pinned': return '📍'
      case 'Roller': return '🔘'
      case 'Free': return '🆓'
      default: return '❓'
    }
  }

  const getSupportColor = (supportType: SMBeamNode['supportType']) => {
    switch (supportType) {
      case 'Fixed': return 'danger'
      case 'Pinned': return 'success'
      case 'Roller': return 'warning'
      case 'Free': return 'secondary'
      default: return 'light'
    }
  }

  // ========== MAIN RENDER ==========

  const getStepIcon = (stepId: string): string => {
    const icons: Record<string, string> = {
      geometry: 'bi-rulers',
      materials: 'bi-layers',
      loads: 'bi-arrow-down-circle',
      results: 'bi-graph-up',
      response: 'bi-activity',
      report: 'bi-file-earmark-text'
    }
    return icons[stepId] || 'bi-circle'
  }
  
  // Mismatched template is handled by the useEffect above (discard + warn).
  // We continue rendering with the default model rather than navigating to a
  // route that may not exist in this standalone app.

  return (
    <div className="beam-designer-page">
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            {/* App-name title + subtitle + material-info badge were removed —
                the navbar already names the app and the materials tab shows
                the spec strip in full. The timber-type selector is the only
                surviving header control, and only the wood app needs it. */}
            <div className="beam-designer-header px-4">
              <DesignManagerBar material={material} model={model} setModel={setModel} getSessionHeaders={getSessionHeaders} onShare={onShareDesign} />
            </div>
            {material === 'timber' && (
              <div className="beam-designer-header px-4">
                <div className="material-group-selector">
                  <span className="material-group-label">{t('beam.timberType.label')}</span>
                  <div className="material-btn-group" role="group" aria-label={t('beam.timberType.selection')}>
                    <button type="button" className={`btn ${timberKind === 'solid' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => selectTimberKind('solid')}>
                      <i className="bi bi-tree me-1"></i>{t('beam.timberType.solid')}
                    </button>
                    <button type="button" className={`btn ${timberKind === 'glulam' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => selectTimberKind('glulam')}>
                      <i className="bi bi-layers me-1"></i>{t('beam.timberType.glulam')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Progress Steps */}
            <div className="progress-steps">
              <div className="row g-2">
                {[
                  { id: 'geometry', titleKey: 'beam.tab.geometry' as TranslationKey },
                  { id: 'materials', titleKey: 'beam.tab.materials' as TranslationKey },
                  { id: 'loads', titleKey: 'beam.tab.loads' as TranslationKey },
                  { id: 'results', titleKey: 'beam.tab.results' as TranslationKey },
                  { id: 'response', titleKey: 'beam.tab.response' as TranslationKey },
                  { id: 'report', titleKey: 'beam.tab.report' as TranslationKey }
                ].map((step) => (
                  <div key={step.id} className="col">
                    <div
                      className={`progress-step-card ${activeStep === step.id ? 'active' : ''}`}
                      onClick={() => setActiveStep(step.id as TabType)}
                      role="button"
                      tabIndex={0}
                      aria-label={t('beam.tab.ariaLabel', { title: t(step.titleKey) })}
                      aria-selected={activeStep === step.id}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveStep(step.id as TabType) }}
                    >
                      <i className={`${getStepIcon(step.id)} progress-step-icon d-block`}></i>
                      <div className="progress-step-title">{t(step.titleKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Main Content Card */}
            <div className="beam-content-card">
              <div className="beam-content-header">
                <h5 className="beam-content-title">
                  <i className={getStepIcon(activeStep)}></i>
                  {activeStep === 'geometry' && t('beam.header.geometry')}
                  {activeStep === 'materials' && t('beam.header.materials')}
                  {activeStep === 'loads' && t('beam.header.loads')}
                  {activeStep === 'response' && t('beam.header.response')}
                  {activeStep === 'report' && t('beam.header.report')}
                  {activeStep === 'results' && t('beam.header.results')}
                </h5>
              </div>
              <div className="beam-content-body">
                  <ErrorBoundary>
                    <SMBeamDesignerTabContent
                      activeStep={activeStep}
                      model={model}
                      setModel={setModel}
                      geometryCallbacks={geometryCallbacks}
                      getFilteredSectionPresets={getFilteredSectionPresets}
                      getSupportIcon={getSupportIcon}
                      getSupportColor={getSupportColor}
                      sectionMaterialStrength={sectionMaterialStrength}
                      supportNotice={supportNotice}
                      onDismissSupportNotice={() => setSupportNotice(null)}
                      materialState={materialState}
                      calculateSelfWeight={calculateResolvedSelfWeight}
                      loadState={loadState}
                      loadCallbacks={loadCallbacks}
                      selectedLimitState={selectedLimitState}
                      setSelectedLimitState={setSelectedLimitState}
                      resultsView={resultsView}
                      result={result}
                      analyzeAllLimitStates={analyzeAllLimitStates}
                      analyzingAll={analyzingAll}
                      analyzeTimoshenko={analyzeTimoshenko}
                      analyzingTimoshenko={analyzingTimoshenko}
                      analysisTheory={analysisTheory}
                      lastAnalysisSummary={lastAnalysisSummary}
                      selectedCombinationId={selectedCombinationId}
                      setSelectedCombinationId={setSelectedCombinationId}
                      responseView={responseView}
                      getSessionHeaders={getSessionHeaders}
                      setResult={setResult}
                      calculateResponse={calculateResponse}
                      calculatingResponse={calculatingResponse}
                      calculateSecondOrderEffects={calculateSecondOrderEffects}
                      calculatingSecondOrderEffects={calculatingSecondOrderEffects}
                      secondOrderEffectsResult={secondOrderEffectsResult}
                      calculateDynamics={calculateDynamics}
                      calculatingDynamics={calculatingDynamics}
                    dynamicsResult={dynamicsResult}
                    sectionCatalogLoading={sectionCatalogLoading}
                    sectionCatalogError={sectionCatalogError}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </div>
        {/* Analysis Status Indicator */}
        {loading && (
          <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1050 }}>
            <div className="analysis-toast toast show" role="alert">
              <div className="toast-header">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">{t('beam.status.loading')}</span>
                </div>
                <strong className="me-auto">{t('beam.status.analysisRunning')}</strong>
              </div>
              <div className="toast-body">
                {t('beam.status.analyzingModel')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BeamDesignerWorkspace
